/**
 * Switch project — the sheet behind the hub's active-project band.
 *
 * Presented as a sheet over a dimmed hub rather than a full screen, because
 * changing project is a modal aside from work, not a place you go. You pick
 * and you are back where you were.
 *
 * "Work without a project" sits in the list as a peer of the projects, not as
 * a dismissal or a back button. Unfiled capture is a supported way to work —
 * a walkthrough that does not belong to anything yet, a site visit before the
 * project exists — and burying it would be telling inspectors to pick a wrong
 * project rather than no project, which is worse data than none.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { FieldProject } from '@kapture/domain';
import { GestureBar, I, Ico, ScreenFrame, StatusBar, T } from '@/components/primitives';
import { useActiveProject, useProjects, useSetActiveProjectMutation } from '@kapture/client-core';

export const Route = createFileRoute('/projects')({
  component: SwitchProjectPage,
  staticData: { mobile: true },
});

function lastWorked(project: FieldProject): string {
  if (project.mySessionCount === 0) return 'none of yours yet';
  const mine = `${project.mySessionCount} of yours`;
  if (!project.lastWorkedAt) return mine;
  const diffMin = Math.round((Date.now() - Date.parse(project.lastWorkedAt)) / 60_000);
  if (diffMin < 1) return `${mine} · last worked just now`;
  if (diffMin < 60) return `${mine} · last worked ${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${mine} · last worked ${diffH}h ago`;
  return `${mine} · last worked ${Math.round(diffH / 24)}d ago`;
}

function SwitchProjectPage() {
  const navigate = useNavigate();
  const { data: projects = [], isLoading } = useProjects();
  const { data: active } = useActiveProject();
  const setActive = useSetActiveProjectMutation();

  function choose(projectId: string | null) {
    setActive.mutate(projectId, { onSuccess: () => navigate({ to: '/' }) });
  }

  return (
    <ScreenFrame tone="light">
      <StatusBar />

      {/* The hub is still there, dimmed — this is an aside, not a departure. */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <button
          type="button"
          aria-label="Close"
          onClick={() => navigate({ to: '/' })}
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(16,18,20,0.45)',
            border: 'none',
            padding: 0,
            width: '100%',
          }}
        />

        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            background: T.surface,
            borderRadius: `${T.r4}px ${T.r4}px 0 0`,
            padding: '10px 16px 20px',
            maxHeight: '100%',
            overflowY: 'auto',
          }}
        >
          <div
            aria-hidden
            style={{
              width: 36,
              height: 4,
              borderRadius: 2,
              background: T.stroke2,
              margin: '0 auto 14px',
            }}
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 2,
            }}
          >
            <h1 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>
              Switch project
            </h1>
            <span style={{ fontFamily: T.mono, fontSize: 10, color: T.textMute }}>
              {projects.length} assigned
            </span>
          </div>
          <p style={{ fontSize: 12, color: T.textDim, margin: '0 0 14px' }}>
            Everything you capture is filed here until you change it.
          </p>

          {isLoading && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: T.textMute, fontSize: 13 }}>
              Loading projects…
            </div>
          )}

          {!isLoading && projects.length === 0 && (
            <div
              style={{
                padding: '16px 12px',
                borderRadius: T.r3,
                background: T.surface2,
                fontSize: 12.5,
                color: T.textDim,
                lineHeight: 1.5,
              }}
            >
              You are not assigned to any projects yet. A manager assigns them in the workspace —
              until then, capture without one.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {projects.map((project) => {
              const current = project.id === active?.id;
              return (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => choose(project.id)}
                  disabled={setActive.isPending}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: 12,
                    minHeight: 64,
                    borderRadius: T.r3,
                    border: current ? `1.5px solid ${T.brand}` : `1px solid ${T.stroke}`,
                    background: current ? T.brandSoft : T.surface,
                    textAlign: 'left',
                    width: '100%',
                    font: 'inherit',
                    color: 'inherit',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 11,
                      flexShrink: 0,
                      background: current ? T.brand : 'transparent',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {current && <Ico d={I.check} size={13} stroke="currentColor" />}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.015em' }}>
                        {project.name}
                      </span>
                      {project.status === 'at-risk' && (
                        <span
                          style={{
                            display: 'inline-flex',
                            padding: '2px 8px',
                            borderRadius: 100,
                            border: `1px solid rgba(207,129,8,0.3)`,
                            background: T.warnSoft,
                            color: T.warnText,
                            fontFamily: T.mono,
                            fontSize: 9.5,
                            fontWeight: 600,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                          }}
                        >
                          At risk
                        </span>
                      )}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontFamily: T.mono,
                        fontSize: 10.5,
                        color: current ? T.brandText : T.textDim,
                        marginTop: 3,
                      }}
                    >
                      {project.code} · {lastWorked(project)}
                    </span>
                  </span>
                  {!current && <Ico d={I.chev} size={16} stroke={T.textMute} />}
                </button>
              );
            })}
          </div>

          <div style={{ height: 1, background: T.stroke, margin: '16px 0' }} />

          <button
            type="button"
            onClick={() => choose(null)}
            disabled={setActive.isPending}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 12,
              minHeight: 56,
              borderRadius: T.r3,
              background: active ? T.surface2 : T.brandSoft,
              border: active ? '1px solid transparent' : `1.5px solid ${T.brand}`,
              width: '100%',
              textAlign: 'left',
              font: 'inherit',
              color: 'inherit',
            }}
          >
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>
                Work without a project
              </span>
              <span
                style={{
                  display: 'block',
                  fontSize: 11.5,
                  color: T.textDim,
                  marginTop: 2,
                  lineHeight: 1.4,
                }}
              >
                Captures still sync. A manager files them later.
              </span>
            </span>
            <Ico d={I.chev} size={16} stroke={T.textMute} />
          </button>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              marginTop: 16,
              paddingTop: 14,
              borderTop: `1px solid ${T.stroke}`,
            }}
          >
            <Ico d={I.info} size={13} stroke={T.textMute} />
            <span style={{ fontFamily: T.mono, fontSize: 10, color: T.textMute }}>
              Held on this device · not shared
            </span>
          </div>
        </div>
      </div>

      <GestureBar />
    </ScreenFrame>
  );
}
